import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Trophy, CheckCircle, AlertCircle, ArrowLeft, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const INITIAL_FORM = {
  fullName: '', phone: '', email: '', age: '',
  isOver18: false, isWAResident: false, legallyEligible: '',
  convictions: '', convictionExplanation: '', preferredStartMonth: '',
  whyCDL: '', consent: false, website: ''
};

const validateGiveawayForm = (formData, recaptchaToken) => {
  if (!formData.isOver18) return 'You must confirm you are 18 or older.';
  if (!formData.isWAResident) return 'You must confirm you are a Washington State resident.';
  if (!formData.legallyEligible) return 'Please select if you are legally eligible.';
  if (!formData.convictions) return 'Please answer the convictions question.';
  if (!formData.preferredStartMonth) return 'Please select a preferred start month.';
  if (!formData.consent) return 'You must accept the rules & terms to enter.';
  if (!recaptchaToken) return 'Please complete the reCAPTCHA verification.';
  return null;
};

export const Giveaway = () => {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateGiveawayForm(formData, recaptchaToken);
    if (validationError) { toast.error(validationError); return; }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/giveaway`, { ...formData, recaptchaToken });
      setSubmitted(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-[#0f1f33] flex items-center justify-center px-4">
        <Card className="max-w-lg w-full text-center shadow-2xl">
          <CardContent className="pt-12 pb-12 px-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">Entry Received!</h2>
            <p className="text-lg text-gray-600 mb-2">Thank you for entering the CDL Scholarship Giveaway!</p>
            <p className="text-xl font-semibold text-[#d97706] mb-8">Winner will be announced July 3rd, 2026</p>
            <Link to="/">
              <Button className="bg-[#1e3a5f] hover:bg-[#152940] text-white px-8 py-3">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Diamond Trucking School
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] via-[#1e3a5f] to-[#0f1f33]">
      {/* Header */}
      <div className="pt-8 pb-4 px-4">
        <div className="container mx-auto max-w-4xl flex items-start justify-between">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="mr-1 w-4 h-4" /> Back to Diamond Trucking School
          </Link>
          <a href="tel:4258902558" className="text-xl font-bold text-white hover:text-[#d97706] transition-colors" data-testid="giveaway-header-phone-link">
            425.890.2558
          </a>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="px-4 pb-8">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-[#d97706] text-white px-5 py-2 rounded-full text-sm font-bold mb-6">
            <Gift className="w-4 h-4" /> LIMITED TIME — June 1 to July 1, 2026
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
            WIN a <span className="text-[#d97706]">FREE</span> Class A CDL Course
          </h1>
          <p className="text-xl text-gray-300 mb-2 max-w-2xl mx-auto">
            Diamond Trucking School is awarding one full tuition scholarship
          </p>
          <p className="text-4xl font-bold text-[#d97706] mb-4">$6,045.90 Value</p>
          <p className="text-base text-gray-400 max-w-xl mx-auto">
            Open to WA residents, 18+. Winner selected after eligibility screening and phone verification by DTS.
          </p>
        </div>
      </div>

      <div className="px-4 pb-16">
        <div className="container mx-auto max-w-4xl grid lg:grid-cols-5 gap-8">
          
          {/* Left: What's Included & Requirements */}
          <div className="lg:col-span-2 space-y-6">
            {/* What's Included */}
            <Card className="shadow-xl border-t-4 border-[#d97706]">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#d97706]" /> What&apos;s Included
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {['Tuition', 'Books & Materials', 'DOT Physical', 'Drug Test'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
                <div className="pt-3 mt-3 border-t">
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Winner is responsible for:</p>
                  <p className="text-xs text-gray-500">Permit fee, final skills test fee, and licensing fee</p>
                </div>
              </CardContent>
            </Card>

            {/* Eligibility Requirements */}
            <Card className="shadow-xl border-t-4 border-[#1e3a5f]">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[#1e3a5f]" /> Eligibility Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {[
                  'Minimum 18 years old',
                  'Able to pass DOT physical',
                  'Able to pass DOT drug test',
                  'Valid Washington driver\'s license',
                  'U.S. citizen or Green Card holder',
                  'Can read, write, speak & understand English',
                  'Must provide driving record (winner only)'
                ].map((req) => (
                  <div key={req} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#1e3a5f] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{req}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Rules Summary */}
            <div className="text-xs text-gray-400 space-y-1 px-1">
              <p>No purchase necessary. No cash value.</p>
              <p>Must use within 180 days from winner selection.</p>
              <p>
                <Link to="/giveaway-rules" className="text-[#d97706] hover:underline">
                  View Official Rules & Terms
                </Link>
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#1e3a5f]">Enter the Giveaway</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input id="fullName" name="fullName" placeholder="Your full legal name" value={formData.fullName} onChange={handleChange} required />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" name="email" type="email" placeholder="you@email.com" value={formData.email} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input id="age" name="age" placeholder="Your age" value={formData.age} onChange={handleChange} required />
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox id="isOver18" checked={formData.isOver18} onCheckedChange={(checked) => setFormData({ ...formData, isOver18: !!checked })} />
                    <Label htmlFor="isOver18" className="text-sm cursor-pointer">I confirm I am 18 years of age or older *</Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox id="isWAResident" checked={formData.isWAResident} onCheckedChange={(checked) => setFormData({ ...formData, isWAResident: !!checked })} />
                    <Label htmlFor="isWAResident" className="text-sm cursor-pointer">I confirm I am a Washington State resident *</Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Are you legally eligible to obtain a CDL in WA? *</Label>
                    <Select value={formData.legallyEligible} onValueChange={(val) => setFormData({ ...formData, legallyEligible: val })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Any disqualifying major convictions? *</Label>
                    <Select value={formData.convictions} onValueChange={(val) => setFormData({ ...formData, convictions: val })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes — explain below</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.convictions === 'Yes' && (
                      <Input name="convictionExplanation" placeholder="Please explain (optional)" value={formData.convictionExplanation} onChange={handleChange} />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Preferred Start Month *</Label>
                    <Select value={formData.preferredStartMonth} onValueChange={(val) => setFormData({ ...formData, preferredStartMonth: val })}>
                      <SelectTrigger><SelectValue placeholder="Select month..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="July">July</SelectItem>
                        <SelectItem value="August">August</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whyCDL">Why do you want a CDL? (1–2 sentences) *</Label>
                    <Textarea id="whyCDL" name="whyCDL" placeholder="Tell us why you want to earn your CDL..." value={formData.whyCDL} onChange={handleChange} rows={3} required />
                  </div>

                  <div className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg">
                    <Checkbox id="consent" checked={formData.consent} onCheckedChange={(checked) => setFormData({ ...formData, consent: !!checked })} className="mt-0.5" />
                    <Label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">
                      I consent to be contacted by Diamond Trucking School regarding this giveaway, and I accept the{' '}
                      <Link to="/giveaway-rules" className="text-[#d97706] hover:underline font-semibold" target="_blank">
                        Official Rules & Terms
                      </Link>. *
                    </Label>
                  </div>

                  {/* Honeypot */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <Input name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleChange} />
                  </div>

                  <div className="flex justify-center" data-testid="giveaway-recaptcha">
                    <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={(token) => setRecaptchaToken(token || '')} onExpired={() => setRecaptchaToken('')} />
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#d97706] hover:bg-[#b45309] text-white py-6 text-lg font-bold" data-testid="giveaway-submit-btn">
                    {isSubmitting ? 'Submitting...' : 'Submit My Entry'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
